/**
 * Strip grouping commas so a displayed number can be parsed/stored as a raw value.
 * Computed formulas must always receive this unformatted form — a comma in the
 * expression ("1,234+5") is not valid math and would abort evaluation.
 */
export const stripThousandSeparators = (value) => {
    if (value === undefined || value === null) return ''
    return String(value).replace(/,/g, '').trim()
}

/**
 * Parse a (possibly formatted) number field into a finite numeric value.
 * Empty / invalid input becomes 0, matching how formulas treat missing operands.
 */
export const toNumericValue = (value) => {
    if (value === undefined || value === null || value === '') return 0
    const numericValue = Number(stripThousandSeparators(value))
    return Number.isFinite(numericValue) ? numericValue : 0
}

/**
 * Parse a field/condition value for numeric comparisons.
 * Empty, blank, and non-numeric values return null so they do not match
 * greater/less comparators (unlike toNumericValue, which treats missing as 0 for formulas).
 */
export const parseComparableNumber = (value) => {
    if (value === undefined || value === null || value === true || value === false) return null
    if (typeof value === 'object') return null
    const stripped = stripThousandSeparators(value)
    if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') return null
    const numericValue = Number(stripped)
    return Number.isFinite(numericValue) ? numericValue : null
}

export const compareNumbers = (currentValue, comparator, expected) => {
    const left = parseComparableNumber(currentValue)
    const right = parseComparableNumber(expected)
    if (left === null || right === null) return false
    switch (comparator) {
        case 'greaterThan':
            return left > right
        case 'greaterThanOrEqualTo':
            return left >= right
        case 'lessThan':
            return left < right
        case 'lessThanOrEqualTo':
            return left <= right
        default:
            return false
    }
}

/**
 * Display-only thousand separators (1,234,567.89). Incomplete typing states
 * ("-", "1.", ".5") are preserved so this can run on every keystroke.
 * Never persist the result — store the stripped value instead.
 */
export const formatWithThousandSeparators = (value) => {
    if (value === undefined || value === null || value === '') return ''
    const str = stripThousandSeparators(value)
    if (str === '' || str === '-' || str === '.' || str === '-.') return str

    const isNegative = str.startsWith('-')
    const unsigned = isNegative ? str.slice(1) : str
    const hasDecimal = unsigned.includes('.')
    const [intPartRaw, decPart] = unsigned.split('.')
    const intPart = intPartRaw === '' ? '0' : intPartRaw
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    let result = (isNegative ? '-' : '') + formattedInt
    if (hasDecimal) {
        result += '.' + (decPart ?? '')
    }
    return result
}

/**
 * Map a caret position from the pre-format string onto the formatted string
 * by counting significant characters (digits, sign, decimal) before the cursor.
 */
export const mapCursorAfterFormat = (oldFormatted, newFormatted, cursor) => {
    const significant = /[0-9.\-]/
    const count = [...String(oldFormatted).slice(0, Math.max(0, cursor))].filter((ch) => significant.test(ch)).length
    if (count <= 0) return 0
    let seen = 0
    for (let i = 0; i < newFormatted.length; i++) {
        if (significant.test(newFormatted[i])) {
            seen++
            if (seen === count) return i + 1
        }
    }
    return newFormatted.length
}

/**
 * Formats a number according to the Indian numbering system (Lakhs and Crores)
 * @param {number} num - The number to format
 * @param {object} options - Formatting options
 * @param {boolean} options.showDecimals - Whether to show decimal places (default: true)
 * @param {number} options.decimalPlaces - Number of decimal places to show (default: 2)
 * @param {boolean} options.abbreviated - Whether to use abbreviated format like "27 L" (default: false)
 * @param {boolean} options.fullWord - Whether to use full words like "27 Lakh" (default: false)
 * @returns {string} Formatted number string
 * 
 * Examples:
 * formatIndianNumber(27277726.94) => "2,72,77,726.94"
 * formatIndianNumber(27277726.94, { abbreviated: true }) => "272.78 L"
 * formatIndianNumber(27277726.94, { fullWord: true }) => "272.78 Lakh"
 * formatIndianNumber(10000000, { abbreviated: true }) => "1 Cr"
 * formatIndianNumber(10000000, { fullWord: true }) => "1 Crore"
 */
export const formatIndianNumber = (num, options = {}) => {
    const {
        showDecimals = true,
        decimalPlaces = 2,
        abbreviated = false,
        fullWord = false
    } = options;

    if (num === null || num === undefined || isNaN(num)) {
        return '0';
    }

    const absNum = Math.abs(num);
    const isNegative = num < 0;

    // For abbreviated or full word format
    if (abbreviated || fullWord) {
        let value, suffix;

        if (absNum >= 10000000) { // 1 Crore = 10,000,000
            value = absNum / 10000000;
            suffix = abbreviated ? 'Cr' : (value === 1 ? 'Crore' : 'Crores');
        } else if (absNum >= 100000) { // 1 Lakh = 100,000
            value = absNum / 100000;
            suffix = abbreviated ? 'L' : (value === 1 ? 'Lakh' : 'Lakhs');
        } else if (absNum >= 1000) { // 1 Thousand = 1,000
            value = absNum / 1000;
            suffix = abbreviated ? 'K' : (value === 1 ? 'Thousand' : 'Thousands');
        } else {
            // For numbers less than 1000, just format with commas
            return formatWithIndianCommas(num, showDecimals, decimalPlaces);
        }

        const formattedValue = showDecimals
            ? value.toFixed(decimalPlaces).replace(/\.?0+$/, '')
            : Math.round(value).toString();

        return `${isNegative ? '-' : ''}${formattedValue} ${suffix}`;
    }

    // Standard Indian comma format (e.g., 2,72,77,726.94)
    return formatWithIndianCommas(num, showDecimals, decimalPlaces);
};

/**
 * Helper function to format number with Indian comma system
 * Indian system: X,XX,XX,XXX (groups of 2 after the first 3 digits from right)
 */
const formatWithIndianCommas = (num, showDecimals = true, decimalPlaces = 2) => {
    if (num === null || num === undefined || isNaN(num)) {
        return '0';
    }

    const isNegative = num < 0;
    const absNum = Math.abs(num);

    // Split into integer and decimal parts
    let [integerPart, decimalPart] = absNum.toString().split('.');

    // Add Indian comma formatting
    // Indian system: groups of 2 from right, except the rightmost group which is 3
    let lastThree = integerPart.substring(integerPart.length - 3);
    let otherNumbers = integerPart.substring(0, integerPart.length - 3);

    if (otherNumbers !== '') {
        lastThree = ',' + lastThree;
    }

    // Add commas every 2 digits for the remaining part
    let formattedInteger = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;

    // Handle decimal part
    let result = formattedInteger;
    if (showDecimals) {
        if (decimalPart) {
            result += '.' + decimalPart.substring(0, decimalPlaces).padEnd(decimalPlaces, '0');
        } else {
            result += '.' + '0'.repeat(decimalPlaces);
        }
    }

    return `${isNegative ? '-' : ''}${result}`;
};

/**
 * Formats currency in Indian Rupee format
 * @param {number} num - The number to format
 * @param {object} options - Formatting options (same as formatIndianNumber)
 * @returns {string} Formatted currency string with ₹ symbol
 */
export const formatIndianCurrency = (num, options = {}) => {
    return '₹' + formatIndianNumber(num, options);
};
